import { Palette, RotateCcw } from "lucide-react";
import WhitelabelUpgradePrompt from "@/components/WhitelabelUpgradePrompt";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/utils";
import { GOOGLE_FONTS } from "@/lib/google-fonts";
import { brandCssVars, darkenHex } from "@/lib/tenant-brand";
import { useTenantBrand } from "@/components/TenantBrandProvider";
import { resolveMediaUrl } from "@/lib/media-api";
import MediaPickerModal from "@/components/MediaPickerModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const BG_DEFAULTS = { background: "#09090B", surface: "#121214", elev: "#1A1A1D" };

const SUPPORT_CATEGORIES = [
  "Branded email sender",
  "DNS / domain setup help",
  "Other whitelabel request",
];

const EMPTY_BRAND = {
  enabled: false,
  mode: "full",
  productName: "",
  logoUrl: "",
  faviconUrl: "",
  adminHostname: "",
  colors: {
    primary: "#FF5608",
    primaryHover: "#E04D07",
    accent: "#60A5FA",
    background: "",
    surface: "",
    elev: "",
  },
  fonts: { heading: "Outfit", body: "DM Sans" },
  showPoweredBy: false,
  useOrgBrandOnNewEvents: true,
  emailSender: { fromEmail: "", fromName: "", replyTo: "" },
  emailSenderVerification: { status: "not_started", domain: "", lastCheckedAt: null },
  customCss: "",
};

function mergeBrandFromApi(b) {
  return {
    ...EMPTY_BRAND,
    ...b,
    colors: { ...EMPTY_BRAND.colors, ...(b.colors || {}) },
    fonts: { ...EMPTY_BRAND.fonts, ...(b.fonts || {}) },
    emailSender: { ...EMPTY_BRAND.emailSender, ...(b.emailSender || {}) },
    emailSenderVerification: {
      ...EMPTY_BRAND.emailSenderVerification,
      ...(b.emailSenderVerification || {}),
    },
  };
}

export default function BrandPanel() {
  const { refreshBrand } = useTenantBrand();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [plan, setPlan] = useState("studio");
  const [brand, setBrand] = useState(EMPTY_BRAND);
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);
  const [faviconPickerOpen, setFaviconPickerOpen] = useState(false);
  const [supportCategory, setSupportCategory] = useState(SUPPORT_CATEGORIES[0]);
  const [supportNotes, setSupportNotes] = useState("");
  const [submittingSupport, setSubmittingSupport] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/workspace/brand`, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        if (cancelled) return;
        setCanEdit(Boolean(data.canEditWhitelabel || data.whitelabelEntitled));
        setPlan(String(data.plan || "studio"));
        const b = data.branding || {};
        setBrand(mergeBrandFromApi(b));
      } catch {
        if (!cancelled) toast.error("Could not load brand settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { emailSender, ...payload } = brand;
      const res = await fetch(`${API_BASE}/api/admin/workspace/brand`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || res.statusText);
      setBrand(mergeBrandFromApi(data.branding || {}));
      await refreshBrand();
      toast.success("Brand kit saved");
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetToFactory = async () => {
    setResetting(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/workspace/brand/reset`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || res.statusText);
      setBrand(mergeBrandFromApi(data.branding || {}));
      await refreshBrand();
      setResetDialogOpen(false);
      toast.success("Brand kit reset to Aivory defaults");
    } catch (err) {
      toast.error(err?.message || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const submitSupportRequest = async () => {
    const notes = supportNotes.trim();
    if (!notes) {
      toast.error("Please describe your request");
      return;
    }
    setSubmittingSupport(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/workspace/brand/support-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ category: supportCategory, notes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || res.statusText);
      setSupportNotes("");
      toast.success("Support request submitted — Aivory ops will follow up");
    } catch (err) {
      toast.error(err?.message || "Request failed");
    } finally {
      setSubmittingSupport(false);
    }
  };

  const previewStyle = brandCssVars({
    enabled: Boolean(brand.enabled),
    colors: {
      ...EMPTY_BRAND.colors,
      ...brand.colors,
      background: brand.colors.background || "",
    },
    fonts: brand.fonts,
  });

  const primaryPickerValue =
    /^#[0-9A-Fa-f]{6}$/.test(String(brand.colors.primary || ""))
      ? brand.colors.primary
      : "#FF5608";

  if (loading) {
    return <div className="text-sm text-[var(--text-secondary)]">Loading brand settings…</div>;
  }

  if (!canEdit) {
    return <WhitelabelUpgradePrompt plan={plan} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl flex items-center gap-2">
            <Palette size={20} /> White-label branding
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
            Rebrand the admin console and login for your team. Full mode hides AivoryEMS; co-brand keeps a small Aivory mark.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
            <AlertDialogTrigger asChild>
              <button type="button" className="btn-secondary text-sm" disabled={saving || resetting}>
                <RotateCcw size={14} aria-hidden />
                Reset to defaults
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="surface border-[var(--border-default)] bg-[var(--bg-elev)] text-[var(--text-primary)]">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading">Reset to factory defaults?</AlertDialogTitle>
                <AlertDialogDescription className="text-[var(--text-secondary)]">
                  Reset all white-label settings to Aivory factory defaults? This cannot be undone without re-configuring.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="btn-primary"
                  disabled={resetting}
                  onClick={(e) => {
                    e.preventDefault();
                    resetToFactory();
                  }}
                >
                  {resetting ? "Resetting…" : "Reset"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <button type="button" className="btn-primary" disabled={saving || resetting} onClick={save}>
            {saving ? "Saving…" : "Save brand kit"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="surface p-6 space-y-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={brand.enabled} onChange={(e) => setBrand((s) => ({ ...s, enabled: e.target.checked }))} />
            Enable workspace branding
          </label>
          {!brand.enabled ? (
            <p className="text-xs text-[var(--text-secondary)] border-l-2 border-[var(--primary)] pl-3 leading-relaxed">
              Turn this on and click <strong className="text-[var(--text-primary)]">Save brand kit</strong> for colors, logo, and fonts to apply across the admin console and login.
            </p>
          ) : null}
          <div>
            <label className="text-overline">Display mode</label>
            <select className="input-dark mt-2 w-full" value={brand.mode} onChange={(e) => setBrand((s) => ({ ...s, mode: e.target.value }))}>
              <option value="full">Full white-label (hide AivoryEMS)</option>
              <option value="cobrand">Co-brand (tenant + Aivory)</option>
            </select>
          </div>
          <div>
            <label className="text-overline">Product name</label>
            <input className="input-dark mt-2 w-full" value={brand.productName} onChange={(e) => setBrand((s) => ({ ...s, productName: e.target.value }))} placeholder="Northbound Events" />
          </div>
          <div>
            <label className="text-overline">Logo</label>
            <div className="mt-2 flex items-center gap-3">
              {brand.logoUrl ? <img src={resolveMediaUrl(brand.logoUrl)} alt="" className="h-10 object-contain" /> : <span className="text-xs text-[var(--text-tertiary)]">No logo</span>}
              <button type="button" className="btn-ghost text-xs" onClick={() => setLogoPickerOpen(true)}>Choose from media</button>
            </div>
          </div>
          <div>
            <label className="text-overline">Favicon</label>
            <div className="mt-2 flex items-center gap-3">
              {brand.faviconUrl ? <img src={resolveMediaUrl(brand.faviconUrl)} alt="" className="h-8 w-8 object-contain" /> : <span className="text-xs text-[var(--text-tertiary)]">Default</span>}
              <button type="button" className="btn-ghost text-xs" onClick={() => setFaviconPickerOpen(true)}>Choose image</button>
            </div>
          </div>
          <div>
            <label className="text-overline">Dedicated admin hostname (optional)</label>
            <input className="input-dark mt-2 w-full font-mono text-sm" value={brand.adminHostname} onChange={(e) => setBrand((s) => ({ ...s, adminHostname: e.target.value }))} placeholder="ops.yourbrand.com" />
            <p className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1">DNS + SSL provisioning is configured by Aivory ops after you save.</p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={brand.useOrgBrandOnNewEvents !== false} onChange={(e) => setBrand((s) => ({ ...s, useOrgBrandOnNewEvents: e.target.checked }))} />
            Apply brand defaults to new events
          </label>
          {brand.mode === "cobrand" ? (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={brand.showPoweredBy} onChange={(e) => setBrand((s) => ({ ...s, showPoweredBy: e.target.checked }))} />
              Show &quot;Powered by AivoryEMS&quot; in footer
            </label>
          ) : null}
        </div>

        <div className="space-y-6">
          <div className="surface p-6 space-y-4">
            <h3 className="font-heading text-lg">Colors & typography</h3>
            <div className="grid grid-cols-2 gap-4">
              <label className="block min-w-0">
                <span className="brand-color-label">Primary</span>
                <input
                  type="color"
                  className="brand-color-input"
                  value={primaryPickerValue}
                  onChange={(e) =>
                    setBrand((s) => ({
                      ...s,
                      colors: {
                        ...s.colors,
                        primary: e.target.value,
                        primaryHover: darkenHex(e.target.value),
                      },
                    }))
                  }
                />
              </label>
              <label className="block min-w-0">
                <span className="brand-color-label">Accent</span>
                <input
                  type="color"
                  className="brand-color-input"
                  value={brand.colors.accent}
                  onChange={(e) => setBrand((s) => ({ ...s, colors: { ...s.colors, accent: e.target.value } }))}
                />
              </label>
            </div>
            <div className="border-t border-[var(--border-subtle)] pt-4 space-y-3">
              <p className="text-xs text-[var(--text-secondary)]">Console backgrounds</p>
              <div className="grid grid-cols-3 gap-4">
                <label className="block min-w-0">
                  <span className="brand-color-label">Base</span>
                  <input
                    type="color"
                    className="brand-color-input"
                    value={brand.colors.background || BG_DEFAULTS.background}
                    onChange={(e) => setBrand((s) => ({ ...s, colors: { ...s.colors, background: e.target.value } }))}
                  />
                </label>
                <label className="block min-w-0">
                  <span className="brand-color-label">Surface</span>
                  <input
                    type="color"
                    className="brand-color-input"
                    value={brand.colors.surface || BG_DEFAULTS.surface}
                    onChange={(e) => setBrand((s) => ({ ...s, colors: { ...s.colors, surface: e.target.value } }))}
                  />
                </label>
                <label className="block min-w-0">
                  <span className="brand-color-label">Elevated</span>
                  <input
                    type="color"
                    className="brand-color-input"
                    value={brand.colors.elev || BG_DEFAULTS.elev}
                    onChange={(e) => setBrand((s) => ({ ...s, colors: { ...s.colors, elev: e.target.value } }))}
                  />
                </label>
              </div>
              <p className="text-[10px] text-[var(--text-tertiary)] leading-relaxed">
                Base is the page background; surface and elevated are cards and panels.
              </p>
            </div>
            <label className="block">
              <span className="text-overline">Heading font</span>
              <select className="input-dark mt-2 w-full" value={brand.fonts.heading} onChange={(e) => setBrand((s) => ({ ...s, fonts: { ...s.fonts, heading: e.target.value } }))}>
                {GOOGLE_FONTS.map((f) => <option key={f.label} value={f.label}>{f.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-overline">Body font</span>
              <select className="input-dark mt-2 w-full" value={brand.fonts.body} onChange={(e) => setBrand((s) => ({ ...s, fonts: { ...s.fonts, body: e.target.value } }))}>
                {GOOGLE_FONTS.map((f) => <option key={f.label} value={f.label}>{f.label}</option>)}
              </select>
            </label>
          </div>

          <div className="surface p-6 space-y-3">
            <h3 className="font-heading text-lg">Custom email sender</h3>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              Add your sending domain below. DNS verification is completed with Aivory ops; status updates when verified.
            </p>
            <div className="flex flex-wrap items-center gap-2 text-xs font-mono uppercase tracking-wider">
              <span className="text-overline">Verification</span>
              <span
                className={
                  brand.emailSenderVerification?.status === "verified"
                    ? "text-[#34D399]"
                    : "text-[var(--text-tertiary)]"
                }
              >
                {brand.emailSenderVerification?.status || "not_started"}
              </span>
            </div>
            <label className="block">
              <span className="text-overline">Sending domain</span>
              <input
                className="input-dark mt-2 w-full font-mono text-sm"
                placeholder="mail.yourcompany.com"
                value={brand.emailSenderVerification?.domain || ""}
                onChange={(e) =>
                  setBrand((s) => ({
                    ...s,
                    emailSenderVerification: {
                      ...(s.emailSenderVerification || {}),
                      domain: e.target.value,
                      status: s.emailSenderVerification?.status || "pending",
                    },
                  }))
                }
              />
            </label>
            {brand.emailSender?.fromEmail ? (
              <div className="rounded-md border border-[var(--border-default)] bg-[var(--bg-elev)] px-3 py-2 text-sm font-mono">
                <span className="text-overline block mb-1">Configured sender</span>
                {brand.emailSender.fromName ? `${brand.emailSender.fromName} ` : ""}
                &lt;{brand.emailSender.fromEmail}&gt;
                {brand.emailSender.replyTo ? (
                  <span className="block text-xs text-[var(--text-tertiary)] mt-1">
                    Reply-To: {brand.emailSender.replyTo}
                  </span>
                ) : null}
              </div>
            ) : null}
            <label className="block">
              <span className="text-overline">Request type</span>
              <select
                className="input-dark mt-2 w-full"
                value={supportCategory}
                onChange={(e) => setSupportCategory(e.target.value)}
              >
                {SUPPORT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <textarea
              className="input-dark w-full min-h-[80px] text-sm"
              placeholder="Describe your domain, desired sender address, or DNS setup needs…"
              value={supportNotes}
              onChange={(e) => setSupportNotes(e.target.value)}
            />
            <button
              type="button"
              className="btn-secondary text-xs"
              disabled={submittingSupport}
              onClick={submitSupportRequest}
            >
              {submittingSupport ? "Submitting…" : "Submit request"}
            </button>
          </div>

          <div className="surface p-6 space-y-3">
            <h3 className="font-heading text-lg">Custom CSS (advanced)</h3>
            <textarea className="input-dark w-full min-h-[100px] font-mono text-xs" value={brand.customCss || ""} onChange={(e) => setBrand((s) => ({ ...s, customCss: e.target.value }))} placeholder=".aivory-tenant-brand .btn-primary { border-radius: 999px; }" />
            <p className="text-[10px] font-mono text-[var(--text-tertiary)]">Scripts, @import, HTML tags, and external URLs are stripped on save. Use layout/CSS properties only.</p>
          </div>

          <div
            className="surface p-6 border border-[var(--border-default)]"
            style={previewStyle}
          >
            <div className="text-overline mb-3">Preview</div>
            <div className="rounded-md border border-[var(--border-default)] overflow-hidden bg-[var(--bg-base)]">
              <div className="px-4 py-3 bg-[var(--bg-elev)] flex items-center gap-2 border-b border-[var(--border-default)]">
                {brand.logoUrl ? <img src={resolveMediaUrl(brand.logoUrl)} alt="" className="h-6 object-contain" /> : null}
                <span className="font-heading font-semibold text-sm">{brand.productName || "Your product"}</span>
              </div>
              <div className="p-4 space-y-2">
                <div className="text-xs px-3 py-2 border-l-2 border-[var(--primary)] bg-[var(--bg-surface)]">Active nav item</div>
                <button type="button" className="btn-primary text-xs">Primary action</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MediaPickerModal
        open={logoPickerOpen}
        onClose={() => setLogoPickerOpen(false)}
        onSelect={(asset) => {
          const url = asset?.url || asset?.publicUrl || "";
          setBrand((s) => ({ ...s, logoUrl: url }));
          setLogoPickerOpen(false);
        }}
        kind="image"
      />
      <MediaPickerModal
        open={faviconPickerOpen}
        onClose={() => setFaviconPickerOpen(false)}
        onSelect={(asset) => {
          const url = asset?.url || asset?.publicUrl || "";
          setBrand((s) => ({ ...s, faviconUrl: url }));
          setFaviconPickerOpen(false);
        }}
        kind="image"
      />
    </div>
  );
}
